export type RequestInterceptorParam = [string, RequestInit]
export type RequestInterceptor = f.Func1<
    RequestInterceptorParam,
    RequestInterceptorParam | Promise<RequestInterceptorParam>
>
export type ResponseInterceptor = f.Func1<Response, Promise<Response>>

/**
 * Pluggable HTTP function with the same shape as the global `fetch`.
 *
 * `BaseRestService` defers the actual network call to whichever fetch is
 * registered via {@link BaseRestService.setAuthorizedFetch}. In every app
 * we register `aos-auth`'s `createAuthorizedFetch(runtime)` once at boot
 * (host on web, `AuthBus` on mobile); that wrapper attaches the
 * `Authorization` header, single-flights silent refresh on 401, and
 * invalidates the session on a second 401.
 *
 * Before the auth runtime is ready (e.g. for the very first config /
 * static-asset request the app makes before `<AuthProvider>` boots), the
 * fallback is the platform `fetch` — same semantics minus the auth
 * header. All API services that need auth derive from this class, so
 * once the host registers, every subsequent service request flows
 * through the authorized fetch automatically.
 */
export type HttpFetch = (input: string, init?: RequestInit) => Promise<Response>

const contentType = 'Content-Type'

export class ApiError extends Error {
    status: number
    body: any

    constructor(status: number, body: any, message?: string) {
        super(message ?? body?.message ?? `Request failed with status ${status}`)
        this.name = 'ApiError'
        this.status = status
        this.body = body
    }
}

export class BaseRestService {
    public static responseInterceptors: ResponseInterceptor[] = []
    public static requestInterceptors: RequestInterceptor[] = []
    public static errorHandlers: f.Func1<Error, Promise<Error>>[] = [
        (e: Error) => Promise.reject(e),
    ]

    /**
     * Defaults to the global `fetch`. Apps replace this with `aos-auth`'s
     * `createAuthorizedFetch(runtime)` so every request automatically
     * carries an `Authorization` header and silently refreshes on 401.
     */
    private static httpFetch: HttpFetch = (input, init) => fetch(input, init)

    /** Inject the authorized fetch implementation (typically from aos-auth). */
    public static setAuthorizedFetch(fn: HttpFetch | null): void {
        BaseRestService.httpFetch = fn ?? ((input, init) => fetch(input, init))
    }

    public static async jsonTransformer<T>(r: Response): Promise<T> {
        const contentType = (r.headers.get('content-type') ?? '').toLowerCase()

        const raw = await r.text()
        const trimmed = raw.trim()
        const trimmedLower = trimmed.toLowerCase()

        const looksLikeHtml =
            trimmedLower.startsWith('<!doctype html') ||
            trimmedLower.startsWith('<html') ||
            contentType.includes('text/html')

        if (looksLikeHtml) {
            throw new ApiError(
                r.status,
                { raw },
                `Expected JSON but received HTML. Check base URL/proxy/routing for ${r.url}`,
            )
        }

        const isJson = contentType.includes('application/json') || contentType.includes('+json') // e.g. application/problem+json

        if (!isJson) {
            throw new ApiError(
                r.status,
                { raw },
                `Expected JSON but received content-type "${contentType || 'unknown'}" for ${
                    r.url
                }`,
            )
        }

        let body: unknown
        try {
            body = trimmed ? JSON.parse(raw) : null
        } catch {
            throw new ApiError(r.status, { raw }, `Invalid JSON returned from ${r.url}`)
        }

        if (!r.ok) {
            throw new ApiError(r.status, body)
        }

        return body as T
    }

    public static rawTransformer(r: Response): Response {
        return r
    }

    public static textTransformer(r: Response): string {
        return r.text() as any
    }

    public static blobTransformer(r: Response): Promise<Blob> {
        return r
            .blob()
            .then(b => new Blob([b], { type: r.headers.get(contentType) || 'text/plain' }))
    }

    public static fileDownloadTransformer(r: Response): Promise<[Blob, string]> {
        return BaseRestService.blobTransformer(r).then(b => {
            const filename = BaseRestService.getFilenameFromHeader(
                r.headers.get('Content-Disposition'),
            )
            if (filename) {
                return [b, filename] as [Blob, string]
            }
            throw new Error('missing filename in download response')
        })
    }

    public static isApiPath = (apiPath: string, url: string) =>
        url.startsWith(apiPath) || url.startsWith('/')

    private static getFilenameFromHeader = (headerValue: string | null) => {
        if (headerValue && headerValue.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            const matches = filenameRegex.exec(headerValue)
            // eslint-disable-next-line eqeqeq
            if (matches != null && matches[1]) {
                return matches[1].replace(/['"]/g, '')
            }
        }
        return undefined
    }

    protected post = (url: string, payload?: any, headers?: Headers): Promise<Response> =>
        this.request(url, {
            method: 'POST',
            body: payload,
            headers: headers || new Headers(),
        })

    protected get = (url: string, headers?: Headers): Promise<Response> => {
        const h = headers ?? new Headers()
        if (!h.has('Accept')) {
            h.set('Accept', 'application/json')
        }
        return this.request(url, { method: 'GET', headers: h })
    }

    protected deleteReq = (url: string, payload?: any, headers?: Headers): Promise<Response> =>
        this.request(url, {
            method: 'DELETE',
            body: payload,
            headers: headers || new Headers(),
        })

    protected patch = (url: string, payload?: any, headers?: Headers): Promise<Response> =>
        this.request(url, {
            method: 'PATCH',
            body: payload,
            headers: headers || new Headers(),
        })

    protected put = (url: string, payload?: any, headers?: Headers): Promise<Response> =>
        this.request(url, {
            method: 'PUT',
            body: payload,
            headers: headers || new Headers(),
        })

    protected postJson = (url: string, payload: any, headers?: Headers): Promise<Response> =>
        this.post(url, payload, this.addJsonHeaders(headers))

    protected putJson = (url: string, payload: any, headers?: Headers): Promise<Response> =>
        this.put(url, payload, this.addJsonHeaders(headers))

    protected patchJson = (url: string, payload: any, headers?: Headers): Promise<Response> =>
        this.patch(url, payload, this.addJsonHeaders(headers))

    protected deleteJson = (url: string, payload: any, headers?: Headers): Promise<Response> =>
        this.deleteReq(url, payload, this.addJsonHeaders(headers))

    protected request = (url: string, init: RequestInit = {}): Promise<Response> =>
        this.runRequestInterceptors(url, init)
            .then(([nextUrl, nextInit]: RequestInterceptorParam) =>
                BaseRestService.httpFetch(nextUrl, nextInit),
            )
            .then(this.responseInterceptors)
            .catch((e: Error) => {
                let current: Promise<Error> = Promise.reject(e)
                BaseRestService.errorHandlers.map(i => {
                    current = current.catch(i)
                })
                return current
            }) as Promise<Response>

    protected postJsonAndTransform<T>(
        url: string,
        payload: any,
        transformer: f.Func1<Response, T | Promise<T>>,
    ): Promise<T> {
        return this.postJson(url, payload).then(transformer)
    }

    protected putJsonAndTransform<T>(
        url: string,
        payload: any,
        transformer: f.Func1<Response, T | Promise<T>>,
    ): Promise<T> {
        return this.putJson(url, payload).then(transformer)
    }

    protected getAndTransform<T>(
        url: string,
        transformer: f.Func1<Response, T | Promise<T>>,
        headers?: Headers,
    ): Promise<T> {
        return this.get(url, headers).then(transformer)
    }

    protected deleteAndTransform<T>(
        url: string,
        transformer: f.Func1<Response, T | Promise<T>>,
    ): Promise<T> {
        return this.deleteReq(url).then(transformer)
    }

    protected patchAndTransform<T>(
        url: string,
        transformer: f.Func1<Response, T | Promise<T>>,
    ): Promise<T> {
        return this.patch(url).then(transformer)
    }

    protected patchJsonAndTransform<T>(
        url: string,
        payload: any,
        transformer: f.Func1<Response, T | Promise<T>>,
    ): Promise<T> {
        return this.patchJson(url, payload).then(transformer)
    }

    protected transformedQuery = <T extends object>(query: T) =>
        Object.entries(query).reduce(
            (acc, [key, value]) => {
                if (Array.isArray(value)) {
                    acc[key] = value.join(',')
                } else {
                    acc[key] = value
                }
                return acc
            },
            {} as Record<string, keyof T | string>,
        )

    private responseInterceptors = (r: Response): Promise<Response> => {
        let current = Promise.resolve(r)
        BaseRestService.responseInterceptors.map(i => {
            current = current.then(i)
        })
        return current
    }

    private runRequestInterceptors = (
        url: string,
        init: RequestInit,
    ): Promise<RequestInterceptorParam> => {
        let current = Promise.resolve([url, init] as RequestInterceptorParam)
        BaseRestService.requestInterceptors.map(i => {
            current = current.then(i)
        })
        return current
    }

    private addJsonHeaders = (headers?: Headers): Headers => {
        const newHeaders = headers || new Headers()
        newHeaders.append(contentType, 'application/json')
        return newHeaders
    }
}
